import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { collection, query, orderBy, limit, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { ScrollText } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { SkeletonTablePage } from '../../components/common/SkeletonLoader';

const AuditLogPage = () => {
    // Correctly get userData from the central layout context
    const { userData } = useOutletContext<{ userData: any }>();
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [userCache, setUserCache] = useState<Record<string, string>>({});
    const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
    const [filterMyActions, setFilterMyActions] = useState(false);

    // Fetch user display name from UID
    const fetchUserName = useCallback(async (uid: string): Promise<string> => {
        if (!uid || uid === 'Valutten') return 'VALUTTEN';
        if (userCache[uid]) return userCache[uid];

        try {
            // Fetch from ROOT users collection (not companies/{id}/users)
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (userDoc.exists()) {
                const data = userDoc.data();
                const displayName = data.displayName
                    || data.email
                    || (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : null);

                if (displayName) {
                    setUserCache(prev => ({ ...prev, [uid]: displayName }));
                    return displayName;
                }
            }

            // If not found, show shortened UID
            console.warn(`User ${uid} not found in users collection`);
            setUserCache(prev => ({ ...prev, [uid]: `User (${uid.substring(0, 8)}...)` }));
            return `User (${uid.substring(0, 8)}...)`;

        } catch (err) {
            console.error('Error fetching user name:', err);
            return `User (${uid.substring(0, 8)}...)`;
        }
    }, [userCache]);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        // Use orderBy to get the most recent entries first. Firestore excludes
        // docs where the ordered field doesn't exist, which naturally filters out
        // legacy entries with empty {} timestamps. This is necessary because without
        // orderBy, limit(N) returns docs in document ID order — UUID-based IDs from
        // the AuditLogger sort differently from auto-IDs, causing recent entries to
        // fall outside the limit window when there are many total docs.
        const logsQuery = query(
            collection(db, `companies/${userData.companyId}/audit_logs`),
            orderBy('timestamp', 'desc'),
            limit(100)
        );

        const unsubscribe = onSnapshot(logsQuery, async (snapshot: any) => {
            const sortedLogs = snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));

            // Fetch user display names for all actors (prefer name over email)
            // Also check log.userId for legacy email connection entries
            const uidsToFetch: string[] = [...new Set<string>(
                sortedLogs
                    .filter((log: any) => (log.actorUid || log.userId) && !log.actorDisplayName)
                    .map((log: any) => (log.actorUid || log.userId) as string)
            )];

            await Promise.all(uidsToFetch.map(fetchUserName));

            setLogs(sortedLogs);
            setLoading(false);
        }, (err: any) => {
            setError("Could not load audit logs.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userData, fetchUserName]);

    const formatDate = (timestamp: any) => {
        if (!timestamp) {
            console.warn('[AuditLog] Missing timestamp in audit log entry');
            return 'Unknown time';
        }

        // Handle Firestore Timestamp object
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            return timestamp.toDate().toLocaleString('en-AU', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        }

        // Handle Firebase server timestamp (seconds + nanoseconds)
        if (timestamp.seconds !== undefined) {
            const date = new Date(timestamp.seconds * 1000);
            return date.toLocaleString('en-AU', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        }

        // Handle regular Date object or timestamp number
        try {
            const date = new Date(timestamp);
            if (!isNaN(date.getTime())) {
                return date.toLocaleString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });
            }
        } catch (e) {
            console.error('[AuditLog] Failed to parse timestamp:', timestamp, e);
        }

        console.warn('[AuditLog] Unrecognized timestamp format:', typeof timestamp, timestamp);
        return 'Unknown time';
    };

    // Get user display name (prefer display name over email)
    // Google Pattern: Check for Valutten system actor FIRST before showing user identity
    const getUserName = (log: any) => {
        // Check for Valutten system actor (admin operations on behalf of companies)
        if (log.actorUid === 'Valutten') return 'VALUTTEN';
        if (log.actorEmail === 'system@valutten.com') return 'VALUTTEN';
        if (log.actorRole === 'system') return 'VALUTTEN';

        // Prefer display name over email for real users
        if (log.actorDisplayName) return log.actorDisplayName;
        if (log.userDisplayName) return log.userDisplayName;
        if (log.actorUid && userCache[log.actorUid]) return userCache[log.actorUid];
        if (log.userId && userCache[log.userId]) return userCache[log.userId];
        if (log.actorEmail) return log.actorEmail;
        // Fallback for script-generated entries (e.g. cleanup scripts)
        if (log.performedBy) return log.performedBy;

        return 'Unknown User';
    };

    // Create a human-readable description of the audit event
    const formatAuditDescription = (log: any) => {
        const user = getUserName(log);
        const action = log.action || log.type || '';

        // Multi-entity upload queued (handles object details)
        if (action === 'MULTI_ENTITY_UPLOAD_QUEUED' || action === 'ADMIN_MULTI_ENTITY_UPLOAD') {
            const files = log.details?.filesQueued || 0;
            const entities = log.details?.entitiesProcessed || 0;
            const month = log.details?.month || '';
            const runNumber = log.details?.runNumber || '';
            return `${user} queued multi-entity upload: ${files} files for ${entities} entities${runNumber ? ` (RUN ${runNumber})` : ''}${month ? ` for ${month}` : ''}.`;
        }

        // Multi-entity upload failed
        if (action === 'MULTI_ENTITY_UPLOAD_FAILED') {
            const error = log.error || 'unknown error';
            return `${user} multi-entity upload failed: ${error}.`;
        }

        // Duplicate file skipped
        if (action === 'COMMISSION_FILE_DUPLICATE_SKIPPED') {
            const fileName = log.metadata?.fileName || 'a file';
            return `${user} uploaded ${fileName} but it was skipped (duplicate file already processed).`;
        }

        // Commission file processing
        if (action === 'COMMISSION_FILE_PROCESSED') {
            const fileName = log.metadata?.fileName || 'a commission file';
            const recordCount = log.metadata?.recordCount || 0;
            const period = log.metadata?.periodMonth || '';
            const duration = log.metadata?.processingDurationMs
                ? ` in ${Math.round(log.metadata.processingDurationMs / 1000)}s`
                : '';

            return `${user} uploaded ${fileName} with ${recordCount} commission records${period ? ` for ${period}` : ''}. Processing completed${duration}.`;
        }

        // Refinance match verified
        if (action === 'REFINANCE_MATCH_VERIFIED') {
            const details = typeof log.details === 'object' ? log.details : {};
            const outcome = details.outcomeType === 'office_transfer' ? 'office transfer' : (details.outcomeType || 'verified');
            const status = details.status || '';
            return `${user} verified refinance match as ${outcome}${status ? ` (${status})` : ''}.`;
        }

        // Refinance detection run
        if (action === 'REFINANCE_DETECTION_RUN') {
            const details = typeof log.details === 'object' ? log.details : {};
            const found = details.matchesFound || 0;
            const inserted = details.newMatchesInserted || 0;
            return `${user} ran refinance detection: ${found} matches found, ${inserted} new matches inserted.`;
        }

        // Admin set loss reason
        if (action === 'ADMIN_SET_LOSS_REASON') {
            const details = typeof log.details === 'object' ? log.details : {};
            const reason = details.reason || 'unknown';
            const loanCount = details.loanCount || (details.loanIds?.length) || 0;
            return `${user} set loss reason "${reason}" on ${loanCount} loan${loanCount !== 1 ? 's' : ''}.`;
        }

        // Broker data attestation
        if (action === 'BROKER_DATA_ATTESTATION') {
            return `${user} attested their broker data.`;
        }

        // User accepted invitation
        if (action === 'USER_ACCEPTED_INVITATION') {
            return `${user} accepted their invitation and joined the company.`;
        }

        // Broker link approval / rejection (admin acts on broker subject)
        if (action === 'BROKER_LINK_MANUALLY_APPROVED') {
            const target = log.metadata?.userEmail || 'a broker';
            const brokerName = log.metadata?.brokerName ? ` as "${log.metadata.brokerName}"` : '';
            return `${user} approved broker link for ${target}${brokerName}.`;
        }
        if (action === 'BROKER_LINK_REJECTED') {
            const target = log.metadata?.userEmail || 'a broker';
            return `${user} rejected broker link for ${target}.`;
        }

        // User status change
        if (action === 'USER_STATUS_CHANGE') {
            const newStatus = log.metadata?.newStatus || log.newValue || '';
            return `${user} changed user status${newStatus ? ` to ${newStatus}` : ''}.`;
        }

        // Individual commission record creation (legacy)
        if (action === 'CREATE_COMMISSION_RECORD') {
            const customer = typeof log.details === 'string' ? log.details?.match(/customer: (.+)/)?.[1] : null;
            return `${user} added a commission record${customer ? ` for ${customer}` : ''}.`;
        }

        // Broker office transfer
        if (action === 'BROKER_OFFICE_TRANSFERRED') {
            const brokerName = log.metadata?.brokerName || 'a broker';
            const newOfficeName = log.metadata?.newOfficeName || 'a new office';
            const reason = log.metadata?.reason || 'organizational change';
            return `${user} transferred ${brokerName} to ${newOfficeName} (reason: ${reason}).`;
        }

        // MFA Setup
        if (action === 'MFA_SETUP_INITIATED') {
            return `${user} enabled two-factor authentication.`;
        }

        // User role changes
        if (action === 'USER_ROLE_UPDATED') {
            const targetUser = log.metadata?.targetUserEmail || 'a user';
            const newRole = log.metadata?.newRole || log.newValue || 'unknown';
            return `${user} changed ${targetUser}'s role to ${newRole}.`;
        }

        // Company operations
        if (action === 'COMPANY_CREATED') {
            const companyName = log.metadata?.companyName || 'a new company';
            return `${user} created ${companyName}.`;
        }

        if (action === 'COMPANY_ARCHIVED') {
            const companyName = log.metadata?.companyName || 'a company';
            return `${user} archived ${companyName}.`;
        }

        if (action === 'COMPANY_ARCHIVED_BATCH') {
            const count = log.metadata?.companyCount || log.companyCount || 0;
            return `${user} archived ${count} compan${count === 1 ? 'y' : 'ies'} in a batch operation.`;
        }

        // Invitation created
        if (action === 'INVITATION_CREATED') {
            const to = log.to || 'a user';
            const roleRaw = log.role || 'a role';
            const roleDisplay = roleRaw.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
            return `${user} invited ${to} as ${roleDisplay}.`;
        }

        // Broker target operations
        if (action === 'BROKER_TARGET_CREATED' || action === 'BROKER_TARGET_UPDATED') {
            const brokerDocId = log.brokerDocId || 'a broker';
            const targetLabel = log.targetLabel || log.targetType || 'a target';
            const verb = action === 'BROKER_TARGET_CREATED' ? 'created' : 'updated';
            return `${user} ${verb} target "${targetLabel}" for ${brokerDocId}.`;
        }
        if (action === 'BROKER_TARGET_DELETED') {
            const brokerDocId = log.brokerDocId || 'a broker';
            const targetLabel = log.targetLabel || log.targetType || 'a target';
            return `${user} deleted target "${targetLabel}" for ${brokerDocId}.`;
        }

        // Broker commission config
        if (action === 'BROKER_COMMISSION_CONFIG_UPDATED') {
            const brokerDocId = log.brokerDocId || 'a broker';
            return `${user} updated commission config for ${brokerDocId}.`;
        }

        // Loan introduction tagging
        if (action === 'LOAN_INTRODUCTION_TYPE_TAGGED') {
            const loanId = log.loanId || 'a loan';
            const introductionType = log.introductionType || 'an introduction type';
            return `${user} tagged loan ${loanId} as ${introductionType}.`;
        }
        if (action === 'LOAN_INTRODUCTION_TYPE_BULK_TAGGED') {
            const loanCount = log.loanCount || 0;
            const introductionType = log.introductionType || 'an introduction type';
            const brokerName = log.brokerName || log.brokerDocId || 'a broker';
            return `${user} bulk-tagged ${loanCount} loan${loanCount === 1 ? '' : 's'} for ${brokerName} as ${introductionType}.`;
        }

        // Broker access permissions
        if (action === 'BROKER_ACCESS_PERMISSIONS_UPDATED') {
            const targetEmail = log.targetEmail || log.targetUserId || 'a user';
            return `${user} updated access permissions for ${targetEmail}.`;
        }

        // Staff operations
        if (action === 'STAFF_CREATED') {
            const staffName = log.staffName || 'a staff member';
            return `${user} added ${staffName} to the staff roster.`;
        }
        if (action === 'STAFF_UPDATED') {
            const staffId = log.staffId || 'a staff member';
            return `${user} updated staff record ${staffId}.`;
        }
        if (action === 'STAFF_DELETED') {
            const staffName = log.staffName || 'a staff member';
            return `${user} removed ${staffName} from the staff roster.`;
        }
        if (action === 'STAFF_ALLOCATIONS_UPDATED') {
            const staffName = log.staffName || 'a staff member';
            const count = log.metadata?.allocationCount || 0;
            return `${user} saved ${count} allocation${count === 1 ? '' : 's'} for ${staffName}.`;
        }

        // Payroll operations
        if (action === 'PAYROLL_CREATED' || action === 'PAYROLL_UPDATED') {
            const staffName = log.staffName || 'a staff member';
            const expenseMonth = log.metadata?.expenseMonth || '';
            const cents = log.metadata?.totalExpensesCents || 0;
            const amount = (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
            const verb = action === 'PAYROLL_CREATED' ? 'recorded' : 'updated';
            return `${user} ${verb} payroll expense for ${staffName}${expenseMonth ? ` (${expenseMonth})` : ''}: ${amount}.`;
        }
        if (action === 'PAYROLL_DELETED') {
            const staffName = log.staffName || 'a staff member';
            const expenseMonth = log.metadata?.expenseMonth || '';
            return `${user} deleted payroll expense for ${staffName}${expenseMonth ? ` (${expenseMonth})` : ''}.`;
        }

        // Cost allocation
        if (action === 'COST_ALLOCATION_RUN') {
            const month = log.metadata?.allocationMonth || '';
            const brokers = log.metadata?.brokersAllocated || 0;
            return `${user} ran cost allocation${month ? ` for ${month}` : ''}: ${brokers} broker${brokers === 1 ? '' : 's'} allocated.`;
        }

        // Clawback backfill
        if (action === 'BACKFILL_CLAWBACKS_STARTED') {
            return `${user} started clawback backfill.`;
        }
        if (action === 'BACKFILL_CLAWBACKS_COMPLETED') {
            const updated = log.updated || 0;
            const matched = log.matched || 0;
            return `${user} completed clawback backfill: ${matched} matched, ${updated} updated.`;
        }

        // Subscription operations
        if (action === 'SUBSCRIPTION_CREATED') {
            const plan = log.metadata?.plan || 'a plan';
            const amount = log.amount || log.metadata?.subscriptionAmount;
            const amountStr = amount ? ` for $${amount}/month` : '';
            return `${user} activated ${plan} subscription${amountStr}.`;
        }

        if (action === 'SUBSCRIPTION_MODIFIED') {
            const changes = log.metadata?.changes || 'subscription details';
            return `${user} modified subscription: ${changes}.`;
        }

        // Welcome emails sent (automated system action)
        if (action === 'WELCOME_EMAILS_SENT') {
            const count = log.count || log.metadata?.count || 'welcome';
            return `${user}: Welcome Emails Sent${typeof count === 'number' ? ` (${count})` : ''}`;
        }

        // Manual commission adjustment
        if (action === 'MANUAL_ADJUSTMENT_CREATED') {
            const brokerName = log.metadata?.brokerName || 'a broker';
            const adjType = (log.metadata?.adjustmentType || 'adjustment').replace(/_/g, ' ');
            const loanId = log.metadata?.loanId || '';
            const cents = log.metadata?.commissionCents || 0;
            const amount = (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
            const period = log.metadata?.periodMonth || '';
            return `${user} added manual ${adjType} for ${brokerName}${loanId ? ` (Loan ${loanId})` : ''}: ${amount}${period ? ` for ${period}` : ''}.`;
        }

        // File deletion
        if (action === 'DELETE_UPLOAD') {
            const fileName = log.fileName || log.metadata?.fileName || 'a file';
            const firestoreRecords = log.commissionRecordsDeleted || 0;
            const bigQueryRecords = log.bigQueryRecordsDeleted || 0;
            const totalRecords = firestoreRecords + bigQueryRecords;

            if (totalRecords > 0) {
                return `${user} deleted ${fileName} (${totalRecords.toLocaleString()} commission records removed).`;
            } else {
                return `${user} deleted ${fileName}.`;
            }
        }

        // Email connection events
        if (action === 'email_connection_created') {
            const email = log.metadata?.connectedEmail || log.userEmail || '';
            return `${user} connected email account${email ? ` (${email})` : ''}.`;
        }
        if (action === 'email_connection_revoked') {
            const email = log.metadata?.connectionEmail || log.connectionEmail || '';
            return `${user} revoked email connection${email ? ` (${email})` : ''}.`;
        }

        // Generic fallback - convert to sentence
        const actionReadable = action
            .split('_')
            .map((word: string) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(' ');

        const detailStr = log.details
            ? typeof log.details === 'object' ? ` - ${JSON.stringify(log.details)}` : ` - ${log.details}`
            : '';
        return `${user} performed action: ${actionReadable}${detailStr}`;
    };

    // Get severity badge color
    const getSeverityColor = (log: any) => {
        // Override severity for successful file uploads (old logs had wrong severity)
        if (log.action === 'COMMISSION_FILE_PROCESSED' && log.result !== 'FAILURE') {
            return 'bg-green-900 text-green-200';
        }

        const severity = log.severity || 'INFO';
        if (severity === 'CRITICAL') return 'bg-red-900 text-red-200';
        if (severity === 'WARNING') return 'bg-orange-900 text-orange-200';
        return 'bg-blue-900 text-blue-200';
    };

    // Get severity icon
    const getSeverityIcon = (log: any) => {
        if (log.action === 'COMMISSION_FILE_PROCESSED' && log.result !== 'FAILURE') {
            return '✓'; // Checkmark for successful uploads
        }

        const severity = log.severity || 'INFO';
        if (severity === 'CRITICAL') return '⚠';
        if (severity === 'WARNING') return '⚡';
        return 'ℹ';
    };

    // Toggle expanded details
    const toggleExpanded = (logId: string) => {
        setExpandedLogs(prev => {
            const newSet = new Set(prev);
            if (newSet.has(logId)) {
                newSet.delete(logId);
            } else {
                newSet.add(logId);
            }
            return newSet;
        });
    };
    
    if (loading) return <SkeletonTablePage tableRows={8} tableColumns={4} />;
    
    if (error) return <div className="p-8 text-center text-red-400"><p>{error}</p></div>;

    const displayedLogs = filterMyActions
        ? logs.filter((log: any) => log.actorUid === userData?.uid || log.userId === userData?.uid)
        : logs;

    return (
        <div className="space-y-6">
            <PageHeader
                icon={ScrollText}
                title="Audit Log"
                subtitle="Activity history for your company"
            />

            {/* Filter controls */}
            <div className="flex items-center gap-4 mb-6">
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={filterMyActions}
                        onChange={(e) => setFilterMyActions(e.target.checked)}
                        className="rounded bg-gray-700 border-gray-600 text-blue-500 focus:ring-blue-500"
                    />
                    My actions only
                </label>
                <span className="text-sm text-gray-500">
                    Showing {displayedLogs.length} of {logs.length} entries
                </span>
            </div>

            {displayedLogs.length === 0 ? (
                <div className="bg-gray-800 p-12 rounded-lg shadow-lg text-center">
                    <h3 className="text-xl font-semibold mb-2">No Activity Yet</h3>
                    <p className="text-gray-400">Important events like file uploads and system changes will appear here.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {displayedLogs.map((log: any) => {
                        const isExpanded = expandedLogs.has(log.id);
                        return (
                            <div
                                key={log.id}
                                className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 hover:border-gray-600 transition-colors"
                            >
                                <div className="p-4">
                                    <div className="flex items-start gap-4">
                                        {/* Icon/Badge */}
                                        <div className={`flex-shrink-0 w-10 h-10 rounded-full ${getSeverityColor(log)} flex items-center justify-center text-lg font-bold`}>
                                            {getSeverityIcon(log)}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            {/* Description */}
                                            <p className="text-white text-base leading-relaxed mb-2">
                                                {formatAuditDescription(log)}
                                            </p>

                                            {/* Timestamp and expand button */}
                                            <div className="flex items-center gap-3">
                                                <p className="text-gray-400 text-sm">
                                                    {formatDate(log.timestamp)}
                                                </p>
                                                <button
                                                    onClick={() => toggleExpanded(log.id)}
                                                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                                                >
                                                    {isExpanded ? '▼ Hide details' : '▶ Show compliance details'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Technical Details (Compliance) */}
                                    {isExpanded && (
                                        <div className="mt-4 pt-4 border-t border-gray-700">
                                            <h4 className="text-sm font-semibold text-gray-300 mb-3">Technical Details (Regulatory Compliance)</h4>
                                            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                                <div>
                                                    <span className="text-gray-400">Audit ID:</span>
                                                    <span className="text-gray-300 ml-2 font-mono text-xs">{log.auditId || log.id}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-400">Result:</span>
                                                    <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                                        log.result === 'SUCCESS' ? 'bg-green-900 text-green-200' :
                                                        log.result === 'FAILURE' ? 'bg-red-900 text-red-200' :
                                                        'bg-gray-700 text-gray-300'
                                                    }`}>
                                                        {log.result || 'SUCCESS'}
                                                    </span>
                                                </div>
                                                {/* Only show Category if it exists and isn't N/A */}
                                                {log.category && log.category !== 'N/A' && (
                                                    <div>
                                                        <span className="text-gray-400">Category:</span>
                                                        <span className="text-gray-300 ml-2">{log.category}</span>
                                                    </div>
                                                )}
                                                <div>
                                                    <span className="text-gray-400">Severity:</span>
                                                    <span className="text-gray-300 ml-2">
                                                        {/* Override wrong severity for successful file uploads */}
                                                        {log.action === 'COMMISSION_FILE_PROCESSED' && log.result !== 'FAILURE'
                                                            ? 'INFO'
                                                            : (log.severity || 'INFO')}
                                                    </span>
                                                </div>
                                                {/* Only show Actor UID if it exists and isn't N/A or Valutten */}
                                                {(log.actorUid || log.userId) && (log.actorUid || log.userId) !== 'N/A' && (log.actorUid || log.userId) !== 'Valutten' && (
                                                    <div>
                                                        <span className="text-gray-400">Actor UID:</span>
                                                        <span className="text-gray-300 ml-2 font-mono text-xs">{log.actorUid || log.userId}</span>
                                                    </div>
                                                )}
                                                <div>
                                                    <span className="text-gray-400">IP Address:</span>
                                                    <span className="text-gray-300 ml-2">{log.ipAddress || 'Not recorded'}</span>
                                                </div>
                                                {log.regulatoryTag && log.regulatoryTag.length > 0 && (
                                                    <div className="col-span-2">
                                                        <span className="text-gray-400">Regulatory Tags:</span>
                                                        <div className="mt-1 flex flex-wrap gap-2">
                                                            {log.regulatoryTag.map((tag: string, idx: number) => (
                                                                <span key={idx} className="px-2 py-1 bg-purple-900 text-purple-200 rounded text-xs font-mono">
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {log.retentionPeriod && (
                                                    <div className="col-span-2">
                                                        <span className="text-gray-400">Retention Period:</span>
                                                        <span className="text-gray-300 ml-2">{log.retentionPeriod} days ({Math.floor(log.retentionPeriod / 365)} years) - Australian Corporations Act 286</span>
                                                    </div>
                                                )}
                                                {log.metadata && Object.keys(log.metadata).length > 0 && (
                                                    <div className="col-span-2">
                                                        <span className="text-gray-400">Additional Metadata:</span>
                                                        <pre className="mt-1 text-xs text-gray-300 bg-gray-900 p-2 rounded overflow-x-auto font-mono">
                                                            {JSON.stringify(log.metadata, null, 2)}
                                                        </pre>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AuditLogPage;